import * as THREE from "three";
import { HAIR_SWING } from "./rider/long-hair";

// Long hair's swing (#227): the fall down the back (`rider/long-hair.ts`'s
// `hair-swing` group, pivoting on the head's centre; a ponytail's tail,
// #234, `rider/ponytail.ts`, pivots on its tie) hangs toward gravity,
// trails when the rider speeds up, turns, flips or lands, and swings back
// through a damped spring. Its direction is kept in world space, so it lags
// behind the body, then clamped in torso space: never in toward the back
// (where the backpack is), only so far out behind or to either side. It
// turns with the head only part of the way, so a glance down the line
// doesn't sweep it over a shoulder. Render-only; at rest the fall hangs
// down the back, lying out over the backpack.

/**
 * How far the fall swings out behind the back, at least (it lies out over
 * the backpack's top) and at most, and to either side (radians, torso space).
 */
export const HAIR_BACK_MIN = 0.1;
export const HAIR_BACK_MAX = 1.0;
export const HAIR_SIDE_MAX = 0.3;
/** How much of the head's turn the fall follows, and the most it turns either way (radians). */
const YAW_SHARE = 0.4;
export const HAIR_YAW_MAX = 0.25;
/** The spring: its natural frequency (rad/s) and damping ratio (under 1 swings past and back). */
const OMEGA = 9;
const ZETA = 0.4;
/** How much the head's acceleration swings the fall (1 would be free hair), and the most it counts (m/s²). */
const ACCEL_SHARE = 0.6;
const ACCEL_MAX = 30;
const G = 9.8;
/** A jump further than this in one frame (a reset, a respawn) starts the hair hanging again. */
const TELEPORT = 1.5;

const DOWN = new THREE.Vector3(0, -1, 0);
const UP = new THREE.Vector3(0, 1, 0);

export class HairSwing {
  /** The fall's direction (world), and how fast its end is moving (per unit length). */
  private dir = new THREE.Vector3(0, -1, 0);
  private vel = new THREE.Vector3();
  private prevPos = new THREE.Vector3();
  private prevVel = new THREE.Vector3();
  private started = false;
  /** The clamped swing in torso space: out behind (+), to the +z side (+), and the turn. */
  back = 0;
  side = 0;
  yaw = 0;

  private qTorso = new THREE.Quaternion();
  private qInv = new THREE.Quaternion();
  private q = new THREE.Quaternion();
  private q2 = new THREE.Quaternion();
  private q3 = new THREE.Quaternion();
  private q4 = new THREE.Quaternion();
  private euler = new THREE.Euler();
  private v = new THREE.Vector3();
  private a = new THREE.Vector3();
  private pos = new THREE.Vector3();

  constructor(
    private swing: THREE.Object3D,
    private head: THREE.Object3D,
    private torso: THREE.Object3D,
  ) {}

  /** The swing for a rig with long hair or a ponytail, or null. */
  static find(head: THREE.Object3D, torso: THREE.Object3D): HairSwing | null {
    const swing = head.getObjectByName(HAIR_SWING);
    return swing ? new HairSwing(swing, head, torso) : null;
  }

  /** Steps the swing after the rider's pose is set for this frame. */
  update(dt: number): void {
    if (dt <= 0) return;
    this.torso.getWorldQuaternion(this.qTorso);
    this.swing.parent!.getWorldPosition(this.pos);
    const jumped = this.pos.distanceTo(this.prevPos) > TELEPORT;
    if (!this.started || jumped) {
      this.prevPos.copy(this.pos);
      this.prevVel.set(0, 0, 0);
      this.vel.set(0, 0, 0);
      // Hanging straight down the back.
      this.dir.copy(DOWN).applyQuaternion(this.qTorso);
      this.started = true;
    }
    // The head's velocity and acceleration from its last two frames.
    this.v.subVectors(this.pos, this.prevPos).divideScalar(dt);
    this.a.subVectors(this.v, this.prevVel).divideScalar(dt);
    if (this.a.length() > ACCEL_MAX) this.a.setLength(ACCEL_MAX);
    this.prevPos.copy(this.pos);
    this.prevVel.copy(this.v);
    // Where free-ish hair would hang: gravity less the share of the acceleration it feels.
    const target = this.a.multiplyScalar(-ACCEL_SHARE).addScaledVector(DOWN, G).normalize();
    // A damped spring on the fall's end, kept on the unit sphere.
    this.vel.addScaledVector(target.sub(this.dir), OMEGA * OMEGA * dt).multiplyScalar(Math.max(0, 1 - 2 * ZETA * OMEGA * dt));
    this.dir.addScaledVector(this.vel, dt).normalize();
    this.vel.addScaledVector(this.dir, -this.vel.dot(this.dir));

    // Into torso space, clamped out over the back.
    this.qInv.copy(this.qTorso).invert();
    const t = this.v.copy(this.dir).applyQuaternion(this.qInv);
    const back = clamp(Math.atan2(t.x, -t.y), HAIR_BACK_MIN, HAIR_BACK_MAX);
    const side = clamp(Math.atan2(t.z, -t.y), -HAIR_SIDE_MAX, HAIR_SIDE_MAX);
    if (back !== Math.atan2(t.x, -t.y) || side !== Math.atan2(t.z, -t.y)) {
      // Held at the limit: the fall rests against it rather than pushing through.
      hangDir(back, side, this.dir).applyQuaternion(this.qTorso);
      this.vel.multiplyScalar(0.5);
      this.vel.addScaledVector(this.dir, -this.vel.dot(this.dir));
    }
    const headYaw = this.head.parent === this.torso ? this.euler.setFromQuaternion(this.head.quaternion, "YZX").y : 0;
    this.pose(back, side, clamp(YAW_SHARE * headYaw, -HAIR_YAW_MAX, HAIR_YAW_MAX));
  }

  /**
   * Sets the fall's group so it hangs `back` out behind the back and `side`
   * toward +z (torso space), turned `yaw` about the torso's up, whatever
   * the head is doing.
   */
  pose(back: number, side: number, yaw: number): void {
    this.back = back;
    this.side = side;
    this.yaw = yaw;
    // The group's parent (in the head's inner group, turned to face the chest) in torso space...
    const inner = this.q4.identity();
    for (let o = this.swing.parent; o && o !== this.head; o = o.parent) inner.premultiply(o.quaternion);
    const parentInTorso = this.q.copy(this.head.quaternion).multiply(inner);
    // ...and where the group should be: at rest (the inner group's turn, the head square), turned, then tipped.
    const turned = this.q3.setFromAxisAngle(UP, yaw).multiply(inner);
    const tipped = this.q2.setFromUnitVectors(DOWN, hangDir(back, side, this.a)).multiply(turned);
    this.swing.quaternion.copy(parentInTorso.invert().multiply(tipped));
  }
}

/** The direction hanging `back` out behind (+x) and `side` toward +z from straight down (torso space). */
function hangDir(back: number, side: number, out: THREE.Vector3): THREE.Vector3 {
  return out.set(Math.tan(back), -1, Math.tan(side)).normalize();
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
